// src/pages/DiagnosticPage.jsx
// Enhanced Firebase diagnostic with fix suggestions and testing

import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseClient';
import { ref, uploadBytes, getBytes, deleteObject } from 'firebase/storage';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';

const DiagnosticPage = () => {
  const [envVars, setEnvVars] = useState({});
  const [firebaseStatus, setFirebaseStatus] = useState({});
  const [storageTest, setStorageTest] = useState({ upload: null, download: null });
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    checkEnvironment();
    checkFirebase();
  }, []);


  const checkEnvironment = () => {
  // ADD THESE DEBUG LOGS HERE ↓
  console.log('🔍 DEBUG - All env vars:', {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY?.substring(0, 10) + '...',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  });
  
  const vars = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  };
  
  // ADD THIS DEBUG LOG HERE ↓
  console.log('🔍 DEBUG - vars object:', vars);
  
  setEnvVars(vars);
};

  const checkFirebase = () => {
    setFirebaseStatus({
      db: !!db,
      storage: !!storage,
      crypto: typeof window !== 'undefined' && !!window.crypto && !!window.crypto.subtle,
    });
  };

  const runStorageTest = async () => {
    setTesting(true);
    const testId = `test-${Date.now()}`;
    const testData = new TextEncoder().encode('Hello Firebase!');

    try {
      // Test Upload
      console.log('[Diagnostic] Testing upload...');
      const uploadRef = ref(storage, `shares/${testId}/test.txt`);
      await uploadBytes(uploadRef, testData);
      setStorageTest(prev => ({ ...prev, upload: { success: true, message: 'Upload successful' } }));
      console.log('[Diagnostic] ✅ Upload successful');

      // Test Download
      console.log('[Diagnostic] Testing download...');
      const downloadedData = await getBytes(uploadRef);
      const downloadedText = new TextDecoder().decode(downloadedData);
      
      if (downloadedText === 'Hello Firebase!') {
        setStorageTest(prev => ({ ...prev, download: { success: true, message: 'Download successful' } }));
        console.log('[Diagnostic] ✅ Download successful');
      } else {
        throw new Error('Downloaded data does not match uploaded data');
      }

      // Cleanup
      await deleteObject(uploadRef);
      console.log('[Diagnostic] ✅ Cleanup successful');

    } catch (err) {
      console.error('[Diagnostic] ❌ Storage test failed:', err);
      
      const errorMessage = err.message || 'Unknown error';
      const errorCode = err.code || 'N/A';
      
      if (!storageTest.upload?.success) {
        setStorageTest(prev => ({
          ...prev,
          upload: {
            success: false,
            message: `Failed: ${errorMessage}`,
            code: errorCode,
            error: err
          }
        }));
      } else {
        setStorageTest(prev => ({
          ...prev,
          download: {
            success: false,
            message: `Failed: ${errorMessage}`,
            code: errorCode,
            error: err
          }
        }));
      }
    } finally {
      setTesting(false);
    }
  };

  const StatusIcon = ({ status }) => {
    if (status === true) return <span className="text-green-500">✅</span>;
    if (status === false) return <span className="text-red-500">❌</span>;
    return <span className="text-yellow-500">⚠️</span>;
  };

  const allEnvVarsSet = Object.values(envVars).every(v => !!v);
  const firebaseReady = firebaseStatus.db && firebaseStatus.storage;
  const storageWorks = storageTest.upload?.success && storageTest.download?.success;

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">🔧 Firebase Diagnostic</h1>
          <p className="text-slate-400">
            Check Firebase configuration and test storage functionality
          </p>
        </div>

        {/* Overall Status */}
        <div className={`p-6 rounded-xl border-2 ${
          allEnvVarsSet && firebaseReady && storageWorks
            ? 'bg-green-900/20 border-green-700'
            : 'bg-red-900/20 border-red-700'
        }`}>
          <h2 className="text-2xl font-bold mb-2">
            {allEnvVarsSet && firebaseReady && storageWorks
              ? '✅ All Systems Operational'
              : '⚠️ Configuration Issues Detected'
            }
          </h2>
          <p className="text-slate-300">
            {allEnvVarsSet && firebaseReady && storageWorks
              ? 'Firebase is properly configured and working!'
              : 'Some issues need to be addressed. See details below.'
            }
          </p>
        </div>

        {/* Environment Variables */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-2xl font-bold mb-4">Environment Variables</h2>
          <div className="space-y-2">
            {Object.entries(envVars).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between p-3 bg-slate-700/50 rounded">
                <code className="text-sm">{key}:</code>
                <div className="flex items-center gap-2">
                  <StatusIcon status={!!value} />
                  <span className={value ? 'text-green-400' : 'text-red-400'}>
                    {value ? 'Set' : 'Missing'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {!allEnvVarsSet && (
            <div className="mt-4 p-4 bg-red-900/30 border border-red-700 rounded">
              <h3 className="font-bold mb-2">🚨 Action Required</h3>
              <p className="text-sm mb-2">Missing environment variables. Add to your <code>.env</code> file:</p>
              <pre className="bg-slate-900 p-3 rounded text-xs overflow-x-auto">
{`REACT_APP_FIREBASE_API_KEY=your-api-key
REACT_APP_FIREBASE_AUTH_DOMAIN=your-project.firebaseapp.com
REACT_APP_FIREBASE_PROJECT_ID=your-project-id
REACT_APP_FIREBASE_STORAGE_BUCKET=your-project.appspot.com`}
              </pre>
              <p className="text-sm mt-2 text-yellow-300">⚠️ Restart dev server after updating .env</p>
            </div>
          )}
        </div>

        {/* Firebase Initialization */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-2xl font-bold mb-4">Firebase Initialization</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded">
              <span>Firestore (db):</span>
              <div className="flex items-center gap-2">
                <StatusIcon status={firebaseStatus.db} />
                <span className={firebaseStatus.db ? 'text-green-400' : 'text-red-400'}>
                  {firebaseStatus.db ? 'Initialized' : 'Not Initialized'}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded">
              <span>Storage:</span>
              <div className="flex items-center gap-2">
                <StatusIcon status={firebaseStatus.storage} />
                <span className={firebaseStatus.storage ? 'text-green-400' : 'text-red-400'}>
                  {firebaseStatus.storage ? 'Initialized' : 'Not Initialized'}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded">
              <span>Web Crypto API:</span>
              <div className="flex items-center gap-2">
                <StatusIcon status={firebaseStatus.crypto} />
                <span className={firebaseStatus.crypto ? 'text-green-400' : 'text-red-400'}>
                  {firebaseStatus.crypto ? 'Available' : 'Not Available'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Storage Test */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Storage Test</h2>
            <button
              onClick={runStorageTest}
              disabled={testing || !firebaseReady}
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {testing ? '⏳ Testing...' : '🧪 Run Test'}
            </button>
          </div>

          {storageTest.upload && (
            <div className="space-y-2">
              <div className={`p-3 rounded border ${
                storageTest.upload.success
                  ? 'bg-green-900/20 border-green-700'
                  : 'bg-red-900/20 border-red-700'
              }`}>
                <div className="flex items-center gap-2">
                  <StatusIcon status={storageTest.upload.success} />
                  <span className="font-semibold">Upload:</span>
                  <span>{storageTest.upload.message}</span>
                </div>
                {storageTest.upload.code && (
                  <p className="text-xs text-slate-400 mt-1">Code: {storageTest.upload.code}</p>
                )}
              </div>

              {storageTest.download && (
                <div className={`p-3 rounded border ${
                  storageTest.download.success
                    ? 'bg-green-900/20 border-green-700'
                    : 'bg-red-900/20 border-red-700'
                }`}>
                  <div className="flex items-center gap-2">
                    <StatusIcon status={storageTest.download.success} />
                    <span className="font-semibold">Download:</span>
                    <span>{storageTest.download.message}</span>
                  </div>
                  {storageTest.download.code && (
                    <p className="text-xs text-slate-400 mt-1">Code: {storageTest.download.code}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {storageTest.upload && !storageTest.upload.success && (
            <div className="mt-4 p-4 bg-red-900/30 border border-red-700 rounded">
              <h3 className="font-bold mb-2">🚨 Storage Error - Action Required</h3>
              <p className="text-sm mb-3">Firebase Storage is not working. Most likely causes:</p>
              
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-semibold text-yellow-300">1. Missing authDomain in .env</p>
                  <p className="text-slate-300">Add this line to your .env file:</p>
                  <pre className="bg-slate-900 p-2 rounded text-xs mt-1">
REACT_APP_FIREBASE_AUTH_DOMAIN=your-project-id.firebaseapp.com
                  </pre>
                </div>

                <div>
                  <p className="font-semibold text-yellow-300">2. Incorrect Storage Rules</p>
                  <p className="text-slate-300">Update Firebase Storage rules to allow shares/ folder:</p>
                  <pre className="bg-slate-900 p-2 rounded text-xs mt-1 overflow-x-auto">
{`rules_version = '2';
service firebase.storage {
  match /b/{bucket}/o {
    match /shares/{shareId}/{allPaths=**} {
      allow read: if true;
      allow write: if true;
    }
  }
}`}
                  </pre>
                </div>

                <div>
                  <p className="font-semibold text-yellow-300">3. CORS Configuration</p>
                  <p className="text-slate-300">If testing locally, ensure CORS is configured for localhost</p>
                </div>
              </div>

              <p className="text-sm mt-3 font-semibold text-blue-300">
                📖 See FIREBASE_FIX_GUIDE.md for detailed instructions
              </p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-2xl font-bold mb-4">🎯 Quick Actions</h2>
          <div className="space-y-3">
            <a
              href="/test-share"
              className="block bg-blue-600 hover:bg-blue-500 text-center px-6 py-3 rounded-lg font-medium transition-colors"
            >
              🧪 Test Share Generator
            </a>
            <a
              href="/"
              className="block bg-green-600 hover:bg-green-500 text-center px-6 py-3 rounded-lg font-medium transition-colors"
            >
              🏠 Back to App
            </a>
          </div>
        </div>

        {/* Firebase Storage Rules Reminder */}
        <div className="bg-blue-900/20 border border-blue-700 rounded-xl p-6">
          <h3 className="text-xl font-bold mb-2">💡 Firebase Storage Rules</h3>
          <p className="text-slate-300 text-sm mb-3">
            Make sure your Firebase Storage rules allow public read/write to the <code className="bg-slate-700 px-1 rounded">shares/</code> path:
          </p>
          <pre className="bg-slate-900 p-4 rounded text-xs overflow-x-auto">
{`rules_version = '2';
service firebase.storage {
  match /b/{bucket}/o {
    match /shares/{shareId}/{allPaths=**} {
      allow read: if true;
      allow write: if true;
    }
  }
}`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPage;